import DataTable from 'v2/components/DataTable';
import EmptyState from 'v2/components/EmptyState';
import LoadingBar from 'v2/components/LoadingBar';
import OutlinedButton from 'v2/components/OutlinedButton';
import useControllerTable from 'v2/layouts/OrganizationWorkspaceLayout/components/ControllerTableSection/hooks/useControllerTable';
import EquipmentRowActions from 'v2/layouts/OrganizationWorkspaceLayout/components/EquipmentRowActions';

import useNavigation from 'hooks/useNavigation';
import usePageRoutes from 'hooks/usePageRoutes';

import useTranslation from './hooks/useTranslation';

import {
    ACTIONS_COLUMN_KEY,
    ACTIONS_COLUMN_WIDTH,
    EMPTY_CELL_TEXT,
    MAKE_COLUMN_KEY,
    MODEL_COLUMN_KEY,
    NAME_COLUMN_KEY,
    SERIAL_NUMBER_COLUMN_KEY,
    TEST_ID
} from './constants';
import type { DataTableColumn, DataTableRow, FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const ControllerTableSection: FunctionComponent<Props> = ({ className = '', testId = TEST_ID }) => {
    const { controllers, handleDelete, handleLoadMore, hasNextPage, isFetchingMore, isLoading } =
        useControllerTable();

    const { navigate } = useNavigation();
    const { editControllerPage } = usePageRoutes();

    const {
        ACTIONS_LABEL,
        DELETE,
        EDIT,
        EMPTY_DESCRIPTION,
        EMPTY_HEADING,
        LOAD_MORE,
        MAKE,
        MODEL,
        NAME,
        SERIAL_NUMBER,
        TABLE_LABEL
    } = useTranslation();

    const columns: DataTableColumn[] = [
        { key: NAME_COLUMN_KEY, label: NAME },
        { key: MAKE_COLUMN_KEY, label: MAKE },
        { key: MODEL_COLUMN_KEY, label: MODEL },
        { key: SERIAL_NUMBER_COLUMN_KEY, label: SERIAL_NUMBER },
        { key: ACTIONS_COLUMN_KEY, label: '', width: ACTIONS_COLUMN_WIDTH }
    ];

    const rows: DataTableRow[] = controllers.map(currentController => ({
        [ACTIONS_COLUMN_KEY]: (
            <EquipmentRowActions
                ariaLabel={ACTIONS_LABEL}
                deleteText={DELETE}
                editText={EDIT}
                onDelete={() => handleDelete(currentController.id)}
                onEdit={() => navigate(editControllerPage(currentController.id))}
            />
        ),
        [MAKE_COLUMN_KEY]: currentController.make || EMPTY_CELL_TEXT,
        [MODEL_COLUMN_KEY]: currentController.model || EMPTY_CELL_TEXT,
        [NAME_COLUMN_KEY]: currentController.nickname || EMPTY_CELL_TEXT,
        [SERIAL_NUMBER_COLUMN_KEY]: currentController.serialNumber || EMPTY_CELL_TEXT
    }));

    if (isLoading) {
        return <LoadingBar className={className} testId={testId} />;
    }

    if (controllers.length === 0) {
        return (
            <EmptyState
                className={className}
                description={EMPTY_DESCRIPTION}
                heading={EMPTY_HEADING}
                testId={testId}
            />
        );
    }

    return (
        <div className={`${className} ${styles.controllerTableSection}`} data-testid={testId}>
            <div className={styles.tableScroll}>
                <DataTable ariaLabel={TABLE_LABEL} columns={columns} rows={rows} />
            </div>

            {hasNextPage ? (
                <OutlinedButton
                    className={styles.loadMore}
                    disabled={isFetchingMore}
                    onClick={handleLoadMore}
                >
                    {LOAD_MORE}
                </OutlinedButton>
            ) : null}
        </div>
    );
};

export default ControllerTableSection;
